import { featureNav, type NavEntry } from "../features";
import { DeploymentHelpButton, DeploymentHelpLink, DeploymentSidebarGroup, DeploymentStaffNav, DeploymentSubscriptionBanner, DeploymentUserMenuItem, deploymentOrgNavItems, useDeploymentOrgNavBadges } from "@backstory/deployment";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, DEV_TOKEN_AUTH, queryKeys, SUBSCRIPTION_EXPIRED_EVENT } from "../api";
import { Logo } from "./Logo";
import { authStore, currentOrg, useAuth } from "../auth/store";
import { ROLE_LABELS, useCan, type Can } from "../auth/roles";
import type { Scope } from "@backstory/protocol";
import { ThemeToggle } from "./ThemeToggle";
import { InboxBell } from "../features/notifications/InboxBell";
import { allowsFeature, useOrgEntitlements } from "../features/billing/entitlements";

const LS_SIDEBAR = "backstory-sidebar-collapsed";

function navGroup(to: string): "observe" | "insights" | "respond" | "more" {
  if (to === "/" || to.startsWith("/sessions") || to.startsWith("/live") || to.startsWith("/visits") || to.startsWith("/backend")) return "observe";
  if (to.startsWith("/dashboard") || to.startsWith("/traffic") || to.startsWith("/issues") || to.startsWith("/errors") || to.startsWith("/heatmaps") || to.startsWith("/time") || to.startsWith("/funnels") || to.startsWith("/releases")) return "insights";
  if (to.includes("/notifications") || to.startsWith("/calls") || to.startsWith("/inbox")) return "respond";
  return "more";
}

const GROUPS: Array<{ id: ReturnType<typeof navGroup>; label: string }> = [
  { id: "observe", label: "Observe" },
  { id: "insights", label: "Insights" },
  { id: "respond", label: "Respond" },
  { id: "more", label: "More" },
];

function NavIcon({ to }: { to: string }) {
  const d =
    to === "/"
      ? "M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1z"
      : to.startsWith("/live")
        ? "M5 6h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2zm14 3 4-2v10l-4-2z"
      : to.startsWith("/backend")
        ? "M4 4h16v6H4zm0 10h16v6H4zM8 7h.01M8 17h.01"
        : to.startsWith("/traffic")
          ? "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3 12h18M12 3c2.5 2.8 3.8 5.8 3.8 9s-1.3 6.2-3.8 9c-2.5-2.8-3.8-5.8-3.8-9s1.3-6.2 3.8-9z"
        : to.startsWith("/dashboard")
          ? "M4 4h7v9H4zm9 0h7v5h-7zm0 7h7v9h-7zM4 15h7v5H4z"
          : to.startsWith("/issues")
            ? "M12 3 3 21h18L12 3zm0 7v4m0 3h.01"
            : to.startsWith("/errors")
              ? "M12 8v5m0 3h.01M4.93 19h14.14A2 2 0 0 0 21 16.27L13.73 4.4a2 2 0 0 0-3.46 0L3 16.27A2 2 0 0 0 4.93 19z"
              : to.startsWith("/time")
                ? "M12 8v4l2.5 1.5M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z"
                : to.startsWith("/heatmaps")
                  ? "M4 20V10m5 10V4m5 16v-8m5 8V8"
                : to.startsWith("/funnels")
                  ? "M4 5h16l-6 7v6l-4 2v-8z"
                  : to.startsWith("/calls")
                    ? "M7 3h4l1 4-2 2a12 12 0 0 0 5 5l2-2 4 1v4a2 2 0 0 1-2 2A16 16 0 0 1 5 7a2 2 0 0 1 2-2z"
                    : to.includes("/notifications")
                      ? "M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9zm6 13a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2z"
                      : to.startsWith("/staff")
                        ? "M4 4h7v7H4zm9 0h7v7h-7zM4 13h7v7H4zm9 0h7v7h-7z"
                        : "M4 6h16M4 12h16M4 18h10";
  return (
    <svg className="nav-ic" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d={d} stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export function Shell() {
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const org = currentOrg(auth);
  const multiOrg = (auth.me?.orgs.length ?? 0) > 1;
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [menu, setMenu] = useState(false);
  const [drawer, setDrawer] = useState(false);
  // Icons-only by default: the rail gives the page its width back, and hovering
  // (or tabbing into) it reveals the labels without changing the layout.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(LS_SIDEBAR) !== "0";
    } catch {
      return true;
    }
  });
  const menuRef = useRef<HTMLDivElement>(null);

  const projects = useQuery({
    queryKey: queryKeys.projectPicker(auth.currentOrgId ?? "none"),
    queryFn: async () => {
      if (DEV_TOKEN_AUTH) {
        try {
          const p = await api.project();
          return [{ id: p.id, name: p.name, publicKey: p.publicKey }];
        } catch {
          /* fall through to org projects */
        }
      }
      const list = await api.orgs.projects(auth.currentOrgId!);
      return list.map((p) => ({ id: p.id, name: p.name, publicKey: p.publicKey }));
    },
    enabled: auth.status === "authed" && !!auth.currentOrgId,
    staleTime: 60_000,
  });
  useEffect(() => {
    if (projects.data) authStore.setProjects(projects.data);
  }, [projects.data]);

  const environments = useQuery({
    queryKey: queryKeys.environments(auth.currentProjectId),
    queryFn: api.environments,
    enabled: auth.status === "authed" && !!auth.currentProjectId,
    staleTime: 60_000,
  });
  const environmentNames = useMemo(() => {
    const names = (environments.data ?? []).filter(Boolean);
    if (auth.currentEnvironment && !names.includes(auth.currentEnvironment)) names.push(auth.currentEnvironment);
    return Array.from(new Set(names)).sort();
  }, [environments.data, auth.currentEnvironment]);

  const scope = { org: auth.currentOrgId, project: auth.currentProjectId, environment: auth.currentEnvironment };
  const previousScope = useRef(scope);
  useEffect(() => {
    const previous = previousScope.current;
    if (previous.org === scope.org && previous.project === scope.project && previous.environment === scope.environment) return;
    previousScope.current = scope;
    // Loading the first project is initial hydration, not a scope transition,
    // so there cannot be cross-scope data to evict yet.
    if (!previous.project) return;
    void queryClient.resetQueries({
      predicate: (query) => {
        const root = query.queryKey[0];
        if (root === "ai") return query.queryKey[1] !== "settings" && query.queryKey[1] !== "usage";
        return root === "sessions" || root === "session" || root === "events" || root === "visit" || root === "insights" || root === "live" || root === "calls" || root === "issues" || root === "assets";
      },
    });
  }, [auth.currentEnvironment, auth.currentOrgId, auth.currentProjectId, queryClient]);

  useEffect(() => {
    if (!menu) return;
    const onDoc = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenu(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menu]);

  useEffect(() => {
    setDrawer(false);
  }, [pathname]);

  useEffect(() => {
    const onExpired = () => {
      if (!auth.currentOrgId || pathname.includes("/billing")) return;
      void navigate({ to: "/orgs/$org/billing", params: { org: auth.currentOrgId } });
    };
    window.addEventListener(SUBSCRIPTION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SUBSCRIPTION_EXPIRED_EVENT, onExpired);
  }, [auth.currentOrgId, navigate, pathname]);

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(LS_SIDEBAR, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const initials = (auth.me?.user.name || auth.me?.user.email || "?").slice(0, 2).toUpperCase();

  const can = useCan();
  const ent = useOrgEntitlements(auth.currentOrgId);
  const mainItems = useMemo(() => {
    const extra = featureNav("main").filter((n) => visibleTo(can, n.scope) && (n.upgrade || allowsFeature(ent.data, n.feature)));
    const sessions: NavEntry = { label: "Sessions", to: "/", section: "main", order: 0 };
    return [sessions, ...extra];
  }, [can, ent.data]);

  const grouped = GROUPS.map((g) => ({ ...g, items: mainItems.filter((n) => navGroup(n.to) === g.id) })).filter((g) => g.items.length > 0);

  const linkParams = (n: NavEntry) => (n.needsOrg && org ? { org: org.id } : org ? { org: org.id } : {});
  const orgId = pathname.match(/^\/orgs\/([^/]+)/)?.[1];
  // A product area that lives under an organization path keeps the product rail.
  const productArea = !!orgId && featureNav("main").some((n) => {
    const path = n.to.replace("$org", orgId);
    return pathname === path || pathname.startsWith(path + "/");
  });
  const orgMenuId = productArea ? undefined : orgId;
  // A platform page takes the sidebar the same way an organization page does.
  const staffMenu = pathname === "/staff" || pathname.startsWith("/staff/");
  const menuTakenOver = !!orgMenuId || staffMenu;

  const nav = orgMenuId ? (
    <OrgSideNav orgId={orgMenuId} onNavigate={() => setDrawer(false)} />
  ) : staffMenu ? (
    <DeploymentStaffNav onNavigate={() => setDrawer(false)} />
  ) : (
    <nav className="side-nav" aria-label="Primary">
      {grouped.map((g) => (
        <div key={g.id} className="side-group">
          <div className="side-group-label">{g.label}</div>
          {g.items.map((n) => {
            const locked = !!n.upgrade && ent.isSuccess && !allowsFeature(ent.data, n.feature);
            return (
              <Link
                key={n.to}
                to={n.to as never}
                params={linkParams(n) as never}
                onClick={() => setDrawer(false)}
                activeOptions={n.to === "/" ? { exact: true } : undefined}
                title={locked ? `${n.label}. Available on ${n.upgrade}.` : n.label}
              >
                <NavIcon to={n.to} />
                <span className={locked ? "side-label side-label-lock" : "side-label"}>
                  {n.label}
                  {locked ? <span className="side-upgrade">Upgrade</span> : null}
                </span>
              </Link>
            );
          })}
        </div>
      ))}
      {org && (
        <div className="side-group">
          <div className="side-group-label">Workspace</div>
          <Link to={can.admin ? "/orgs/$org/settings" : "/orgs/$org/members"} params={{ org: org.id }} onClick={() => setDrawer(false)} title="Organization">
            <NavIcon to="/orgs" />
            <span className="side-label">Organization</span>
          </Link>
          <DeploymentHelpLink onSelect={() => setDrawer(false)} />
        </div>
      )}
      <DeploymentSidebarGroup staff={!!auth.me?.user.staff} onSelect={() => setDrawer(false)} />
    </nav>
  );

  return (
    <div className={`app${menuTakenOver ? " org-menu" : collapsed ? " sidebar-collapsed" : ""}${drawer ? " drawer-open" : ""}`}>
      <aside className="sidebar" id="app-sidebar">
        <Link to="/" className="brand side-brand" onClick={() => setDrawer(false)}>
          <Logo size={22} />
          <span className="side-label">
            Backstory
            <span className="tagline">The story behind every bug.</span>
          </span>
        </Link>
        {auth.me && auth.me.orgs.length > 0 && (
          <div className="mobile-switcher" aria-label="Current workspace">
            {multiOrg && (
              <label>
                <span>Organization</span>
                <select aria-label="Organization in menu" value={auth.currentOrgId ?? ""} onChange={(e) => authStore.setOrg(e.target.value)} autoComplete="off">
                  {auth.me.orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </label>
            )}
            <label>
              <span>Project</span>
              <select aria-label="Project in menu" value={auth.currentProjectId ?? ""} onChange={(e) => authStore.setProject(e.target.value)} disabled={!auth.projects.length} autoComplete="off">
                {auth.projects.length === 0 && <option value="">No projects</option>}
                {auth.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label>
              <span>Environment</span>
              <select aria-label="Environment in menu" value={auth.currentEnvironment ?? ""} onChange={(e) => authStore.setEnvironment(e.target.value || undefined)} disabled={!auth.currentProjectId || environments.isPending} autoComplete="off">
                <option value="">All environments</option>
                {environmentNames.map((environment) => <option key={environment} value={environment}>{environment}</option>)}
              </select>
            </label>
          </div>
        )}
        {nav}
        {!menuTakenOver && <button
          type="button"
          className="btn ghost collapse-btn"
          onClick={toggleCollapsed}
          aria-pressed={!collapsed}
          aria-label={collapsed ? "Keep sidebar expanded" : "Collapse sidebar"}
          title={collapsed ? "Keep sidebar expanded" : "Collapse sidebar"}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d={collapsed ? "M9 6l6 6-6 6" : "M15 6l-6 6 6 6"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <span className="side-label">{collapsed ? "Keep expanded" : "Collapse"}</span>
        </button>}
      </aside>
      {drawer && <button type="button" className="drawer-scrim" aria-label="Close menu" onClick={() => setDrawer(false)} />}
      <header className="topbar">
        <button type="button" className="btn ghost icon-btn hamburger" aria-label="Open menu" aria-controls="app-sidebar" aria-expanded={drawer} onClick={() => setDrawer(true)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
        <span className="topbar-title">
          <Logo size={18} /> Backstory
        </span>
        <span className="spacer" />
        {auth.me && auth.me.orgs.length > 0 && (
          <div className="switcher">
            {multiOrg && (
              <select aria-label="Organization" value={auth.currentOrgId ?? ""} onChange={(e) => authStore.setOrg(e.target.value)} autoComplete="off">
                {auth.me.orgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            )}
            <select aria-label="Project" value={auth.currentProjectId ?? ""} onChange={(e) => authStore.setProject(e.target.value)} disabled={!auth.projects.length} autoComplete="off">
              {auth.projects.length === 0 && <option value="">No projects</option>}
              {auth.projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <select aria-label="Environment" value={auth.currentEnvironment ?? ""} onChange={(e) => authStore.setEnvironment(e.target.value || undefined)} disabled={!auth.currentProjectId || environments.isPending} autoComplete="off">
              <option value="">All environments</option>
              {environmentNames.map((environment) => <option key={environment} value={environment}>{environment}</option>)}
            </select>
          </div>
        )}
        {org?.settings.phi && (
          <span className="badge warn" title="This organization processes PHI; strict controls apply">
            PHI
          </span>
        )}
        {org && <DeploymentHelpButton />}
        {org && <InboxBell />}
        <ThemeToggle />
        {auth.me && (
          <div className="user-menu" ref={menuRef}>
            <button className="btn ghost" onClick={() => setMenu(!menu)} aria-haspopup="menu" aria-expanded={menu} aria-label="Account menu">
              <span className="avatar">{initials}</span>
            </button>
            {menu && (
              <div className="menu" role="menu">
                <div className="muted small" style={{ padding: "6px 10px" }}>
                  {auth.me.user.email}
                </div>
                <Link to="/settings/profile" role="menuitem" onClick={() => setMenu(false)}>
                  Profile
                </Link>
                <DeploymentUserMenuItem staff={!!auth.me.user.staff} owner={org?.role === "owner"} onSelect={() => setMenu(false)} />
                <button
                  role="menuitem"
                  onClick={async () => {
                    setMenu(false);
                    await authStore.logout();
                    void navigate({ to: "/login" });
                  }}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </header>
      <main className="main">
        <DeploymentSubscriptionBanner />
        <Outlet />
      </main>
    </div>
  );
}

/** A role sees a nav entry when it holds the entry's scope; admins see all. */
function visibleTo(can: Can, scope: Scope | undefined): boolean {
  return !scope || can.admin || can.has(scope);
}

type OrgNavItem = { to: string; label: string; group?: string; scope?: Scope; feature?: string };

/**
 * Organization pages and the scope each one needs. Members is the one page
 * every role can open (the list is readable by any member), so it is where a
 * non-administrator lands.
 */
const ORG_NAV: OrgNavItem[] = [
  { to: "settings", label: "General", group: "Organization", scope: "admin" },
  { to: "features", label: "Features", scope: "admin" },
  { to: "members", label: "Members" },
  { to: "billing", label: "Usage & billing", scope: "billing" },
  { to: "keys", label: "SDK keys", group: "Access", scope: "tokens" },
  { to: "sso", label: "Single sign-on", scope: "admin", feature: "sso" },
  { to: "networks", label: "Allowed networks", scope: "admin", feature: "ip_allowlist" },
  { to: "hipaa", label: "HIPAA", group: "Compliance", scope: "admin", feature: "hipaa" },
  { to: "desks", label: "Support desks", group: "Support", scope: "admin" },
  { to: "audit", label: "Audit log", scope: "audit" },
  ...deploymentOrgNavItems.map((i) => ({ ...i, scope: "admin" as Scope })),
];

/** Organization pages reached by link rather than from the menu. They are gated the same way. */
const ORG_UNLISTED: OrgNavItem[] = [{ to: "tokens", label: "API tokens", scope: "tokens" }];

/**
 * Organization pages use the app sidebar, so this only decides whether the
 * page itself is shown. A role that cannot use the page sees why instead of a
 * form full of controls that all end in 403. The API stays authoritative.
 */
export function OrgLayout({ orgId, children }: { orgId: string; children: React.ReactNode }) {
  const can = useCan();
  const auth = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hidden = [...[...ORG_NAV, ...ORG_UNLISTED].map((i) => ({ path: `/orgs/${orgId}/${i.to}`, scope: i.scope })), ...featureNav("org").map((n) => ({ path: n.to.replace("$org", orgId), scope: n.scope }))].find(
    (p) => p.scope && !visibleTo(can, p.scope) && (pathname === p.path || pathname.startsWith(p.path + "/")),
  );
  if (!hidden) return children;
  return (
    <div className="card section" role="note" data-testid="role-blocked">
      <h2 style={{ marginTop: 0 }}>Not available to your role</h2>
      <p className="muted">
        You are {can.role ? `${/^[aeiou]/i.test(ROLE_LABELS[can.role]) ? "an" : "a"} ${ROLE_LABELS[can.role]}` : "a member"} in {currentOrg(auth)?.name ?? "this organization"}. This page needs the{" "}
        <span className="mono">{hidden.scope}</span> permission, which owners and admins have. See <Link to="/orgs/$org/members" params={{ org: orgId }}>Members</Link> for what each role can do.
      </p>
    </div>
  );
}

/** The organization list, drawn in the app sidebar while an organization page is open. */
function OrgSideNav({ orgId, onNavigate }: { orgId: string; onNavigate: () => void }) {
  const can = useCan();
  const ent = useOrgEntitlements(orgId);
  const badges = useDeploymentOrgNavBadges(orgId, can.admin);
  const items = ORG_NAV.filter((i) => visibleTo(can, i.scope) && allowsFeature(ent.data, i.feature));
  const features = featureNav("org").filter((n) => visibleTo(can, n.scope) && allowsFeature(ent.data, n.feature));
  const groups: { label: string; links: { key: string; label: string; to: string; badge: number }[] }[] = [];
  for (const item of items) {
    const label = item.group ?? groups.at(-1)?.label ?? "Organization";
    const group = item.group || groups.length === 0 ? { label, links: [] } : groups[groups.length - 1]!;
    if (group.links.length === 0) groups.push(group);
    group.links.push({ key: item.to, label: item.label, to: `/orgs/$org/${item.to}`, badge: badges[item.to] ?? 0 });
  }
  if (features.length > 0) {
    groups.push({ label: "Features", links: features.map((n) => ({ key: n.to, label: n.label, to: n.to, badge: 0 })) });
  }
  return (
    <nav className="side-nav" aria-label="Organization settings">
      <div className="side-group">
        <Link to="/" activeOptions={{ exact: true }} onClick={onNavigate}>
          <span className="side-label">Back to sessions</span>
        </Link>
      </div>
      {groups.map((g) => (
        <div key={g.label} className="side-group">
          <div className="side-group-label">{g.label}</div>
          {g.links.map((l) => (
            <Link key={l.key} to={l.to as never} params={{ org: orgId } as never} onClick={onNavigate} title={l.label} aria-label={l.badge ? `${l.label}, ${l.badge} waiting` : undefined}>
              <span className="side-label">{l.label}</span>
              {l.badge > 0 && <span className="nav-count" aria-hidden="true">{l.badge}</span>}
            </Link>
          ))}
        </div>
      ))}
    </nav>
  );
}
